// Client for the anonymous rules embed API (Rules/Endpoints/RuleEmbedEndpoints.cs).
// The server delivers RESOLVED rules — templates already expanded server-side —
// so `definition` is the evaluator grammar verbatim; this client only unwraps
// the wire envelope and drops rows too malformed to evaluate.

import type { Rule } from "../../../rules";

export interface FetchDesignerRulesOptions {
  /** Base URL of the deployment. Empty = same origin. */
  baseUrl?: string;
  designerId: string;
  /** Short language code ("en", "dk") — the server resolves each rule's
   *  message DVar into `messageText` in this language. */
  lang?: string;
  signal?: AbortSignal;
}

/** One wire row: owner metadata + the evaluator rule JSON. */
interface RuleWireRow {
  id?: unknown;
  ownerType?: unknown;
  ownerId?: unknown;
  definition?: unknown;
}

/**
 * Fetch the full rule set a Designer embed evaluates: product rules first,
 * then the designer's own rules (author order matters — first-wins applies
 * across the concatenation, and the server already ordered them). Anonymous +
 * rate-limited, same auth model as the catalog. Malformed rows are skipped
 * with a console.warn — one bad rule never takes the configurator down.
 */
export async function fetchDesignerRules(opts: FetchDesignerRulesOptions): Promise<Rule[]> {
  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const params = new URLSearchParams();
  if (opts.lang) params.set("lang", opts.lang);
  const qs = params.toString();
  const url =
    `${base}/api/embed/rules/designer/${encodeURIComponent(opts.designerId)}` +
    (qs ? `?${qs}` : "");

  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal: opts.signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`fetchDesignerRules failed: ${res.status} ${res.statusText} ${text}`);
  }
  const body = (await res.json()) as { rules?: RuleWireRow[] };
  return mapWireRows(body.rules ?? []);
}

/** Map wire rows → Rule[]. `definition` is normally an object (the server
 *  serialises a JsonObject) but a JSON string is tolerated too. Rows whose
 *  definition can't evaluate (no `when` / non-array `then`) are skipped. */
function mapWireRows(rows: RuleWireRow[]): Rule[] {
  const rules: Rule[] = [];
  for (const row of rows) {
    let definition = row.definition;
    if (typeof definition === "string") {
      try {
        definition = JSON.parse(definition);
      } catch {
        console.warn("[SceneDesigner rules] skipping rule row — definition is unparseable JSON:", row.id);
        continue;
      }
    }
    if (definition === null || typeof definition !== "object" || Array.isArray(definition)) {
      console.warn("[SceneDesigner rules] skipping rule row — definition is not an object:", row.id);
      continue;
    }
    const def = definition as Partial<Rule> & Record<string, unknown>;
    if (def.when == null || !Array.isArray(def.then)) {
      console.warn("[SceneDesigner rules] skipping rule row — definition lacks when/then:", row.id);
      continue;
    }
    // Backfill identity/scope when the author left them off: the row id keeps
    // the trace/first-wins bookkeeping stable, and missing scope means
    // "product" (the evaluator only special-cases scope === "scene").
    const id = typeof def.id === "string" && def.id
      ? def.id
      : typeof row.id === "string" && row.id
        ? row.id
        : "";
    if (!id) {
      console.warn("[SceneDesigner rules] skipping rule row — no usable id:", row);
      continue;
    }
    rules.push({
      ...def,
      id,
      scope: def.scope === "scene" ? "scene" : "product",
      when: def.when,
      then: def.then,
    } as Rule);
  }
  return rules;
}
